import { useEffect } from "react";
import { createElement } from "react";
import { useRouter } from "next/router";
import cx from "classnames";
import { useSelector } from "react-redux";
import { RootState } from "store";
import { Portal } from "react-portal";
import { AnimatePresence, motion } from "framer-motion";
import { useTheme } from "styled-components";
import Image from "next/image";
import { InternalLink } from "components/atoms/InternalLink";
import { paths } from "enum/paths.enum";
import { FinancialCompany } from "enum/financial-company.enum";
import { useServicesNewTag } from "components/pages/Services/useServicesNewTag";
import { PermissionCheck } from "utils/PermissionCheck/PermissionCheck";
import { isUserAdmin } from "utils/isUserAdmin";
import {
  isUserAdminForSalesAccounts,
  isUserSalesPeopleForSalesAccounts,
} from "utils/isUserAdminForSalesAccounts";
import { canSalesAccountSeeExceptions } from "components/pages/Exceptions/exceptions.constants";
import { canSeeExceptionsV2 } from "components/pages/ExceptionsV2/exceptionsV2.constants";
import { PICKUP_ELIGIBLE_API_PATHS } from "constants/pickup-eligible-carriers.const";
import { IconType } from "react-icons";
import {
  FaBox,
  FaTag,
  FaUpload,
  FaTruck,
  FaCog,
  FaQuestion,
  FaChartBar,
  FaExclamationTriangle,
  FaFolderOpen,
  FaShippingFast,
  FaTimes,
} from "react-icons/fa";
// Same wordmark assets as the app sidebar (see the naming note there: the
// "dark-mode" wordmark is dark ink, so it belongs to the light theme).
import WordmarkLightTheme from "../../../../public/images/dark-mode-icon-uncollapsed.svg";
import WordmarkDarkTheme from "../../../../public/images/light-mode-icon-uncollapsed.svg";

const DrawerLink = ({
  icon,
  label,
  isActive,
  href,
  onNavigate,
  isNew,
}: {
  icon: IconType;
  label: string;
  isActive: boolean;
  href: string;
  onNavigate: () => void;
  /** Shows a "New" tag, as on the desktop sidebar. */
  isNew?: boolean;
}) => {
  const theme = useTheme();
  return (
    <InternalLink href={href} onClick={onNavigate}>
      <div
        className={cx(
          "flex items-center gap-3 rounded-md px-3 h-11 text-sm font-medium leading-none cursor-pointer transition-all",
          // Same states as the desktop sidebar links.
          isActive ? "bg-gray4 text-gray12" : "text-gray11 hover:bg-gray3 hover:text-gray12",
        )}
      >
        <span className="text-base">{createElement(icon)}</span>
        <span>{label}</span>
        {isNew && (
          <span className="px-[5px] py-[3px] text-[10px] font-bold leading-none text-white rounded-full bg-red9">
            New
          </span>
        )}
      </div>
    </InternalLink>
  );
};

/**
 * Phone-only navigation drawer, opened by the header menu button. Mirrors the
 * link set and permission gating of the desktop app sidebar, which is not
 * rendered at phone widths.
 */
export const MobileMenuDrawer = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  const { pathname } = useRouter();
  const showServicesNew = useServicesNewTag();
  const theme = useTheme();

  const { theme: themeName } = useSelector(
    (state: RootState) => state.tableSlice,
  );
  const wordmarkLogo =
    themeName === "dark" ? WordmarkDarkTheme : WordmarkLightTheme;

  const { loggedInUser, financialCompany } = useSelector(
    (state: RootState) => state.authSlice,
  );
  const { settings } = useSelector((state: RootState) => state.settingsSlice);
  const { shippingWhitelists } = useSelector(
    (state: RootState) => state.shippingWhitelistsSlice,
  );

  const permissions = new PermissionCheck({ user: loggedInUser });

  const canSeeExceptions =
    isUserAdminForSalesAccounts(loggedInUser) ||
    isUserSalesPeopleForSalesAccounts(loggedInUser) ||
    canSalesAccountSeeExceptions(settings);

  const containsUpsOrFedex = shippingWhitelists.filter((el) =>
    PICKUP_ELIGIBLE_API_PATHS.includes(el.supplier.apiPath),
  );

  const quotesHref = isUserAdmin(loggedInUser)
    ? paths.SHIPMENTS_INDEX
    : paths.QUOTES_INDEX;
  const quotesActive = isUserAdmin(loggedInUser)
    ? pathname === paths.SHIPMENTS_INDEX
    : pathname === paths.QUOTES_INDEX;

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  return (
    <Portal>
      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              key="mobile-menu-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="fixed inset-0"
              style={{ backgroundColor: "rgba(20, 24, 35, 0.4)", zIndex: 60 }}
              onClick={onClose}
            />
            <motion.div
              key="mobile-menu-panel"
              initial={{ x: -300 }}
              animate={{ x: 0 }}
              exit={{ x: -300 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
              className="fixed left-0 top-0 bottom-0 flex flex-col gap-1 px-3 py-6 overflow-y-auto"
              style={{
                width: 280,
                zIndex: 61,
                backgroundColor: theme.navigationBackgroundColor,
                boxShadow: theme.shadows["2xl"],
              }}
            >
              <div className="flex items-center px-2 pb-4">
                <Image
                  src={wordmarkLogo}
                  alt="Connexx"
                  className="h-auto"
                  style={{ width: 120 }}
                />
                <div className="flex-1" />
                <button
                  onClick={onClose}
                  aria-label="Close menu"
                  className="w-9 h-9 flex items-center justify-center rounded-md text-gray9 hover:text-gray12"
                >
                  <FaTimes size={16} />
                </button>
              </div>

              {permissions.shipments.canView() && (
                <DrawerLink
                  icon={FaBox}
                  label="Shipments"
                  href={paths.SHIPMENTS_INDEX + "?status=READY"}
                  isActive={pathname === paths.SHIPMENTS_INDEX}
                  onNavigate={onClose}
                />
              )}
              <DrawerLink
                icon={FaChartBar}
                label="Intelligence"
                href={paths.INTELLIGENCE_INDEX}
                isActive={pathname === paths.INTELLIGENCE_INDEX}
                onNavigate={onClose}
              />
              {financialCompany !== FinancialCompany.GlobalAmerican && (
                <DrawerLink
                  icon={FaShippingFast}
                  label="Services"
                  href={paths.SERVICES_INDEX}
                  isActive={pathname === paths.SERVICES_INDEX}
                  onNavigate={onClose}
                  isNew={showServicesNew}
                />
              )}
              <DrawerLink
                icon={FaTag}
                label="Quotes"
                href={quotesHref}
                isActive={quotesActive}
                onNavigate={onClose}
              />
              {permissions.bulkShipments.canView() && (
                <DrawerLink
                  icon={FaUpload}
                  label="Uploads"
                  href={paths.BULK_SHIPMENTS_INDEX}
                  isActive={pathname === paths.BULK_SHIPMENTS_INDEX}
                  onNavigate={onClose}
                />
              )}
              {permissions.pickup.canView() && containsUpsOrFedex.length > 0 && (
                <DrawerLink
                  icon={FaTruck}
                  label="Pickups"
                  href={paths.PICKUPS_INDEX}
                  isActive={pathname === paths.PICKUPS_INDEX}
                  onNavigate={onClose}
                />
              )}
              {canSeeExceptions && (
                <DrawerLink
                  icon={FaExclamationTriangle}
                  label="Exceptions"
                  href={paths.EXCEPTIONS_INDEX}
                  isActive={pathname === paths.EXCEPTIONS_INDEX}
                  onNavigate={onClose}
                />
              )}
              {canSeeExceptionsV2(loggedInUser) && (
                <DrawerLink
                  icon={FaExclamationTriangle}
                  label="Exceptions v2"
                  href={paths.EXCEPTIONS_V2_INDEX}
                  isActive={pathname === paths.EXCEPTIONS_V2_INDEX}
                  onNavigate={onClose}
                />
              )}
              <DrawerLink
                icon={FaFolderOpen}
                label="Support Cases"
                href={paths.CASES_INDEX}
                isActive={pathname.startsWith(paths.CASES_INDEX)}
                onNavigate={onClose}
              />

              <div className="flex-1" />

              {permissions.settings.canEdit() && (
                <DrawerLink
                  icon={FaCog}
                  label="Settings"
                  href={paths.COMPANY_SETTINGS_GENERAL}
                  isActive={
                    pathname.startsWith("/settings") &&
                    pathname !== paths.HELP_SETTINGS
                  }
                  onNavigate={onClose}
                />
              )}
              <DrawerLink
                icon={FaQuestion}
                label="Help"
                href={paths.HELP_SETTINGS}
                isActive={pathname === paths.HELP_SETTINGS}
                onNavigate={onClose}
              />
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </Portal>
  );
};
